import { useQuery } from '@tanstack/react-query';
import * as Ramda from 'ramda';
import type { GetPresetsRequest } from '../../../../../api/rater.api';
import { getPresetsByProduct } from '../../../../../api/rater.api';
import { PrimeXProductTypes, ProductTypes } from '../../../../../types';
import type { ProductType, AccountDto } from '../../../../../types';
import { getPresetSecurityQuestionsByProduct } from '../utils/primeX.util';
import { useAPIErrorHandler } from '../../../../../components/hooks/useAPIErrorHandler';

interface QuoteCardDependenciesProps {
  effectiveDate: string | number;
  accountData: AccountData;
  productType: ProductType;
  limit: number;
  deductible: number;
  agencyId: string;
  queryOptions?: Record<string, unknown>;
}

interface AccountData extends Partial<AccountDto> {
  accountId?: string;
}

const useGetQuoteOptionPresets = ({
  effectiveDate,
  accountData,
  productType,
  limit,
  deductible,
  agencyId,
  queryOptions,
}: QuoteCardDependenciesProps) => {
  const handleApiError = useAPIErrorHandler();
  const isPrimeXProduct = Object.values(PrimeXProductTypes).some(
    Ramda.equals(productType)
  );
  const PRESET_NAME =
    PRESET_NAME_MAPPING[productType as keyof typeof PRESET_NAME_MAPPING];

  const presetPayload = {
    accountId: accountData?.id || accountData?.accountId,
    industryClass: accountData?.industryClass,
    isFranchisee: accountData?.isFranchise,
    franchiseSystemConnectivity: accountData?.franchiseSystemConnectivity,
    naicsCode: accountData?.naicsCode,
    revenue: accountData?.revenue,
    totalRevenue: accountData?.totalRevenue,
    state: accountData?.state,
    yearEstablished: accountData?.yearEstablished,
    preset: PRESET_NAME,
    product: productType,
    selectedLimit: limit,
    selectedDeductible: deductible,
    agencyId,
    effectiveDate,
    ...getPresetSecurityQuestionsByProduct(
      accountData as AccountDto,
      productType
    ),
  };

  return useQuery({
    queryKey: [
      'quote-option',
      {
        PRESET_NAME,
        limit,
        effectiveDate,
        productType,
        naics: accountData?.naicsCode,
        presetPayload,
      },
    ],
    queryFn: () =>
      getPresetsByProduct(
        productType,
        accountData?.naicsCode as number,
        PRESET_NAME,
        {
          data: presetPayload as GetPresetsRequest,
        }
      ),
    select: (response) => {
      return response.data;
    },
    onError: handleApiError('Unable to retrieve coverages at this time'),
    keepPreviousData: true,
    retry: 2,
    enabled:
      Boolean(productType) &&
      Boolean(accountData) &&
      Boolean(accountData.naicsCode) &&
      Boolean(isPrimeXProduct),
    ...queryOptions,
  });
};

export default useGetQuoteOptionPresets;

export const PRESET_NAME_MAPPING = Object.freeze({
  [ProductTypes.p100_pro]: 'BASIC',
  [ProductTypes.prime_one]: 'ELITE',
  [ProductTypes.prime_tech]: 'ELITE',
});
